import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import VolumeDrivers from '../components/ContactCenter/VolumeDrivers';
import { fmtCount, fmtPct } from '../components/ContactCenter/format';
import { btn, c, card, tbl } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Repeat callers — how much of the phone volume comes from
// people calling back, per pod, with the most frequent callers and why they
// call. The old report's cleaning, unchanged (services/ccRepeatCallers.js):
// valid numbers only, Athens' own numbers out, a call within 24 hours of a
// missed callback out, a redial within 2 hours of an abandon out.
//
// Holders of the phone-reveal permission see the full number on every row that
// has one; everybody else sees the last four digits. It used to be a "Show"
// button per row, and the same permission decided who got the button — what
// changed is that seeing the list no longer costs a click per caller, not who
// is allowed to see it.
//
// The consequence, which is not visible on the page: there is no longer an
// audit row per number, because there is no longer a per-number action to
// record. `REPORT_VIEWED` on this module is what says who opened the list.

const DATA_FLOOR = '2026-05-19';
function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}
const addDays = (ymd, n) => new Date(Date.parse(`${ymd}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

function ago(iso, includesToday) {
  if (!iso) return '—';
  const t = new Date(iso);
  if (!includesToday) {
    return new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(t);
  }
  const h = (Date.now() - t.getTime()) / 3_600_000;
  return h < 1 ? `${Math.max(1, Math.round(h * 60))}m ago` : h < 48 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)}d ago`;
}

// The number cell. The server sends `number` only to someone holding the reveal
// permission, and only for a caller it actually has one for, so this draws what
// it was given rather than deciding anything: a full number if one arrived, the
// last four otherwise.
//
// The full number REPLACES the masked form rather than following it. Showing
// "…1324 (888) 564-1324" prints the same four digits twice and makes the cell
// the widest in the table, which is what would push it into wrapping.
function CallerNumber({ row }) {
  if (row.number) return <span style={s.number}>{row.number}</span>;
  return <span>{row.masked}</span>;
}

export default function ContactCenterRepeatCallers() {
  const { user } = useAuth();
  const canReveal = !!user?.contactCenter?.canRevealPhone;
  const today = pacificToday();
  const [mode, setMode] = useState('7');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [scope, setScope] = useState('_org');
  const range = mode === 'custom' ? (custom.start && custom.end ? custom : null)
    : { start: addDays(today, -Number(mode)), end: today };
  const q = useQuery({
    queryKey: ['cc-repeat', range?.start, range?.end],
    queryFn: () => api.get('/contact-center/repeat-callers', { params: range }),
    enabled: !!range,
    refetchInterval: 5 * 60 * 1000,
  });
  const d = q.data;
  const drill = d?.drill[scope] || d?.drill._org;
  const max = Math.max(1, ...(d?.perPod || []).map((p) => p.repeatCallPct || 0));

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title="Repeat callers"
        subtitle={d ? `${d.window.start} to ${d.window.end} · ${fmtCount(d.org.totalCalls)} calls from ${fmtCount(d.org.uniqueCallers)} numbers` : 'Callers who call again'}
        actions={(
          <div style={s.row} role="group" aria-label="Window">
            {[['7', 'Last 7 days'], ['30', 'Last 30 days'], ['custom', 'Custom']].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)} style={mode === k ? s.pillOn : s.pill}>{l}</button>
            ))}
            {mode === 'custom' && (
              <>
                <input id="cc-rep-start" type="date" aria-label="Start date" min={DATA_FLOOR} max={today} value={custom.start}
                  onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                <input id="cc-rep-end" type="date" aria-label="End date" min={DATA_FLOOR} max={today} value={custom.end}
                  onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
              </>
            )}
          </div>
        )}
      />
      {q.isLoading && range && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load repeat callers: {q.error.message}</div>}
      {d?.window.clamped && <div style={s.notice}>Conversation history starts on {d.dataFloor}; the window starts there.</div>}
      {d && (
        <>
          <section style={s.card} aria-label="Repeat calls">
            <div style={s.kpi}>
              <div><div style={s.big}>{fmtPct(d.org.repeatCallPct)}</div><div style={s.sub}>of calls came from numbers that called {d.meta.minCalls}+ times</div></div>
              <div><div style={s.big}>{fmtPct(d.org.repeatCallerPct)}</div><div style={s.sub}>of numbers called {d.meta.minCalls}+ times</div></div>
            </div>
            <div role="list" aria-label="Pods">
              {/* The two figures in these rows had no headers, so a reader had
                  to infer that the number on the right was a percentage of
                  calls rather than of callers — the two differ here and both
                  are on this page. Calls moves out of the pod's name and into a
                  column of its own so it can carry one. */}
              <div style={s.podHead} aria-hidden="true">
                <span>Pod</span>
                <span style={s.podHeadNum}>Calls</span>
                <span />
                <span style={s.podHeadNum}>Repeat share</span>
                <span style={s.podHeadNum}>vs all pods</span>
              </div>
              {/* Five cells, like the pod rows. It used to render two into a
                  four-column grid, which put the organization's percentage in
                  the BAR column — one track left of where the same figure sits
                  on every row beneath it. */}
              <button type="button" onClick={() => setScope('_org')} style={{ ...s.podRow, ...(scope === '_org' ? s.podOn : {}) }}>
                <span style={s.podName}>All pods</span>
                <span style={s.podNum}>{fmtCount(d.org.totalCalls)}</span>
                <span style={s.barTrack}><span style={{ ...s.bar, width: `${((d.org.repeatCallPct || 0) / max) * 100}%` }} /></span>
                <span style={s.podNum}>{fmtPct(d.org.repeatCallPct)}</span>
                <span />
              </button>
              {d.perPod.map((p) => (
                <button key={p.pod} type="button" role="listitem" onClick={() => setScope(p.pod)} style={{ ...s.podRow, ...(scope === p.pod ? s.podOn : {}) }}
                  aria-label={`${p.pod} ${fmtCount(p.calls)} calls ${fmtPct(p.repeatCallPct)} repeat share`}>
                  <span style={s.podName}>{p.pod}</span>
                  <span style={s.podNum}>{fmtCount(p.calls)}</span>
                  <span style={s.barTrack}><span style={{ ...s.bar, width: `${((p.repeatCallPct || 0) / max) * 100}%` }} /></span>
                  <span style={s.podNum}>{fmtPct(p.repeatCallPct)}</span>
                  <span style={{ ...s.delta, color: p.repeatCallPct > d.org.repeatCallPct ? c.dangerText : c.successText }}>
                    {p.repeatCallPct == null || d.org.repeatCallPct == null ? '' : `${p.repeatCallPct > d.org.repeatCallPct ? '+' : '−'}${Math.abs(p.repeatCallPct - d.org.repeatCallPct).toFixed(1)} pts`}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section style={s.card} aria-label="Top repeat callers">
            <h2 style={s.h2}>{scope === '_org' ? 'All pods' : scope} · top repeat callers</h2>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Number</th><th style={s.thNum}>Calls</th><th style={s.thNum}>Days</th><th style={s.th}>Last call</th>
                <th style={s.th}>Top reason</th><th style={s.th}>City</th><th style={s.th}>Pods</th>
              </tr></thead>
              <tbody>
                {drill.topRepeaters.map((r) => (
                  <tr key={r.revealToken}>
                    <td style={s.td}><CallerNumber row={r} /></td>
                    <td style={s.num}>{fmtCount(r.calls)}</td>
                    <td style={s.num}>{fmtCount(r.days)}</td>
                    <td style={s.td}>{ago(r.lastCallAt, d.window.includesToday)}</td>
                    <td style={s.td}>{r.topDriver || '—'}</td>
                    <td style={s.td}>{r.city || '—'}</td>
                    <td style={s.td}>{r.pods.join(', ')}</td>
                  </tr>
                ))}
                {!drill.topRepeaters.length && <tr><td colSpan={7} style={s.td}>No repeat callers in this window.</td></tr>}
              </tbody>
            </table>
          </section>

          {/* Why they call, as the donut the Board uses for the same question
              rather than the run-on sentence this was: five shares joined by
              middots read as a caption, so the ranking that is the actual
              finding had to be recovered by the reader. `pct` and `n` are
              mapped to the component's own names — it is the Board's shape and
              this page's server predates it. */}
          {drill.driverMix.length > 0 && (
            <VolumeDrivers
              title="Why repeat callers call"
              centerLabel="Coded calls"
              drivers={drill.driverMix.map((m) => ({ name: m.name, sharePct: m.pct, volume: m.n }))}
              other={drill.driverMixOther
                ? { sharePct: drill.driverMixOther.pct, volume: drill.driverMixOther.n } : null}
              total={drill.driverMixTotal}
              rangeLabel={scope === '_org' ? 'all pods' : scope}
              footnote={`Wrap-up codes on calls from numbers that called ${d.meta.minCalls} or more times in `
                + 'this window — not every call. Codes that are not a reason for calling are left out, so each '
                + 'share is of the calls that carry a real one, and the center is that count.'}
            />
          )}

          {drill.cityRepeat && (
            <section style={s.card} aria-label="Repeat calls by city">
              <h2 style={s.h2}>{scope} · by city</h2>
              <table style={s.table}>
                <thead><tr><th style={s.th}>City</th><th style={s.thNum}>Calls</th><th style={s.thNum}>Repeat calls</th><th style={s.thNum}>Repeat %</th></tr></thead>
                <tbody>
                  {drill.cityRepeat.map((r) => (
                    <tr key={r.city}><td style={s.td}>{r.city}</td><td style={s.num}>{fmtCount(r.calls)}</td>
                      <td style={s.num}>{fmtCount(r.repeatCalls)}</td><td style={s.num}>{fmtPct(r.repeatCallPct)}</td></tr>
                  ))}
                </tbody>
              </table>
              <p style={s.sub}>Cities with fewer than 100 calls are grouped. The city comes from the caller&rsquo;s account or zip code, where Genesys recorded one.</p>
            </section>
          )}

          <p style={s.foot}>
            Voice and VIP calls. Left out before counting: {fmtCount(d.meta.invalidExcluded)} calls without a valid 10-digit number;
            {' '}{fmtCount(d.meta.athensExcluded)} from Athens&rsquo; own numbers; {fmtCount(d.meta.callbackMissExcluded)} made within 24 hours
            of a callback that did not reach them; {fmtCount(d.meta.abandonRedialExcluded)} redials within 2 hours of hanging up in the queue.
            {canReveal
              ? 'Full numbers are shown because you hold the phone-reveal permission; a caller we hold no number for shows its last four digits.'
              : 'Numbers show as their last four digits.'}
          </p>
        </>
      )}
    </div>
  );
}

// Pod, calls, the bar, repeat share, and the gap to the organization. The bar
// takes what is left so the fixed columns stay put as the panel widens — the
// figures are compared down the column, which only works if the column does not
// move. Calls used to live inside the pod's name; it is its own track now so the
// header above it means something.
const POD_COLS = '170px 78px 1fr 96px 80px';

const s = {
  page:    { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:     { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  pill:    { ...btn.secondaryXs },
  pillOn:  { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:   { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong },
  card:    { ...card.base, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  kpi:     { display: 'flex', gap: '40px', flexWrap: 'wrap' },
  big:     { fontSize: '28px', fontWeight: 650, color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  sub:     { fontSize: '11.5px', color: c.textMuted, margin: 0 },
  // One template for the header and every row, referenced rather than repeated.
  // A header whose columns are written out a second time is a header that lines
  // up until the next edit to either one, and the whole point of adding it is
  // that the figures underneath are read against it.
  podRow:  { all: 'unset', cursor: 'pointer', display: 'grid', gridTemplateColumns: POD_COLS, alignItems: 'center', gap: '10px', padding: '6px 8px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', fontSize: '12.5px' },
  podHead: { display: 'grid', gridTemplateColumns: POD_COLS, alignItems: 'center', gap: '10px',
    padding: '0 8px 4px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: c.textMuted, borderBottom: `1px solid ${c.border}`, marginBottom: '2px' },
  podHeadNum: { textAlign: 'right' },
  podOn:   { background: c.surfaceSunk },
  podName: { fontWeight: 600, color: c.textStrong },
  podNum:  { textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.textStrong, fontWeight: 600 },
  barTrack:{ height: '8px', background: c.surfaceSunk, borderRadius: '4px', overflow: 'hidden' },
  bar:     { display: 'block', height: '100%', background: c.navy, borderRadius: '4px' },
  delta:   { fontSize: '11px', textAlign: 'right' },
  h2:      { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  // nowrap so a full number is never broken across two lines in a narrow
  // column; tabular figures so the numbers line up down the column.
  number:  { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  th:      { ...tbl.thSm, textAlign: 'left' },
  thNum:   { ...tbl.thRSm },
  td:      { ...tbl.tdSm, color: c.textStrong },
  num:     { ...tbl.tdRSm, color: c.textStrong },
  notice:  { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:   { fontSize: '13px', color: c.dangerText },
  foot:    { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
};
